<template>
  <button
    class="MenuModalButton"
    v-bind="{disabled}"
  >
    <div class="MenuModalButton__iconContainer">
      <Icon
        class="MenuModalButton__icon"
        :name="iconName"
        :size="iconSize"
      />
    </div>
    <strong
      class="MenuModalButton__title"
      data-testid="title"
    >
      <Transition>
        <span :key="title">{{ title }}</span>
      </Transition>
    </strong>
    <div
      class="MenuModalButton__subtitle"
      data-testid="subtitle"
    >
      <Transition>
        <span :key="subtitle">{{ subtitle }}</span>
      </Transition>
    </div>
  </button>
</template>

<script lang="ts" setup>
import { IconName } from "@/components/Icon.types";
import Icon from "@/components/Icon.vue";
import { computed } from "vue";

const {
  iconName,
} = defineProps<{
  iconName: keyof typeof IconName,
  title: string,
  subtitle: string,
  disabled?: boolean,
}>();

const iconSize = computed(() => {
  switch (iconName) {
    case IconName.personRunning:
    case IconName.personWithArmsRaised:
      return "xlarge";
    default:
      return "large";
  }
});
</script>

<style lang="scss" scoped>
.MenuModalButton {
  position: relative;

  display: grid;
  grid: repeat(2, min-content) / min-content auto;
  gap: 2px 12px;
  justify-items: start;
  padding: 16px;

  text-align: left;

  & + &::before {
    position: absolute;
    left: 16px;
    right: 16px;
    bottom: 100%;

    border-top: solid 1px var(--themeColorNeutral100);

    content: "";
  }
}

.MenuModalButton__iconContainer {
  display: grid;
  grid-row: span 2;
  place-items: center;
  width: 44px;
  aspect-ratio: 1;

  border: solid 2px var(--themeColorBlack);
  border-radius: 8px;
  background-color: var(--themeColorSecondary);
  color: var(--themeColorSecondaryForeground);

  transition: 350ms;
  transition-property: background-color, color, border-color;

  .MenuModalButton:disabled & {
    border-color: var(--themeColorNeutral400);
    background-color: var(--themeColorNeutral150);
    color: var(--themeColorNeutral500);
  }
}

.MenuModalButton__title,
.MenuModalButton__subtitle {
  position: relative;
  top: var(--fontSafariOffset);
  transition: color 350ms;

  .MenuModalButton:disabled & {
    color: var(--themeColorNeutral500);
  }

  :deep(span) {
    display: block;
    transition: 350ms;
    transition-property: opacity, transform;

    &.v-leave-active {
      position: absolute;
      top: 0;
      left: 0;
    }

    &.v-enter-from,
    &.v-leave-to {
      opacity: 0;
    }

    &.v-enter-from {
      transform: translateX(1em);
    }

    &.v-leave-to {
      transform: translateX(-1em);
    }
  }
}

.MenuModalButton__title {
  align-self: end;

  font: var(--fontWeightMedium) 16px / 1 var(--fontFamilyNormal);
  font-stretch: var(--fontWidthExtended);
  white-space: nowrap;
}

.MenuModalButton__subtitle {
  align-self: start;

  color: var(--themeColorNeutral600);

  font: var(--fontWeightMedium) 14px / 18px var(--fontFamilyNormal);
  font-stretch: var(--fontWidthExtended);
}
</style>
